import store from "@/core/services";

// Import store modules
import position_module, {
  GET_ITEMS as GET_POSITIONS,
  ITEMS as POSITION_ITEMS,
  MODULE_NAME as POSITION_MODULE,
} from "@/core/services/store/position.module";

import branch_module, {
  GET_ITEMS as GET_BRANCHES,
  ITEMS as BRANCH_ITEMS,
  MODULE_NAME as BRANCH_MODULE,
} from "@/core/services/store/branch.module";

import department_module, {
  GET_ITEMS as GET_DEPARTMENTS,
  ITEMS as DEPARTMENT_ITEMS,
  MODULE_NAME as DEPARTMENT_MODULE,
} from "@/core/services/store/department.module";

import region_module, {
  GET_ITEMS as GET_REGIONS,
  ITEMS as REGION_ITEMS,
  MODULE_NAME as REGION_MODULE,
} from "@/core/services/store/region.module";

import group_module, {
  GET_ITEMS as GET_GROUPS,
  ITEMS as GROUP_ITEMS,
  MODULE_NAME as GROUP_MODULE,
} from "@/core/services/store/user_group.module";

import title_module, {
  GET_ITEMS as GET_TITLES,
  ITEMS as TITLE_ITEMS,
  MODULE_NAME as TITLE_MODULE,
} from "@/core/services/store/title.module";

const _POSITION_MODULE = POSITION_MODULE;
const _BRANCH_MODULE = BRANCH_MODULE;
const _DEPARTMENT_MODULE = DEPARTMENT_MODULE;
const _REGION_MODULE = REGION_MODULE;
const _GROUP_MODULE = GROUP_MODULE;
const _TITLE_MODULE = TITLE_MODULE;

export default {
  beforeCreate() {
    function registerStoreModule(moduleName, storeModule) {
      if (!(store && store.state && store.state[moduleName])) {
        store.registerModule(moduleName, storeModule);
      }
    }

    registerStoreModule(_POSITION_MODULE, position_module);
    registerStoreModule(_BRANCH_MODULE, branch_module);
    registerStoreModule(_DEPARTMENT_MODULE, department_module);
    registerStoreModule(_REGION_MODULE, region_module);
    registerStoreModule(_GROUP_MODULE, group_module);
    registerStoreModule(_TITLE_MODULE, title_module);
  },

  computed: {
    // Store getters for different modules
    positions() {
      return store.getters[POSITION_MODULE + "/" + POSITION_ITEMS];
    },
    branches() {
      return store.getters[BRANCH_MODULE + "/" + BRANCH_ITEMS];
    },
    departments() {
      return store.getters[DEPARTMENT_MODULE + "/" + DEPARTMENT_ITEMS];
    },
    regions() {
      return store.getters[REGION_MODULE + "/" + REGION_ITEMS];
    },
    groups() {
      return store.getters[GROUP_MODULE + "/" + GROUP_ITEMS];
    },
    titles() {
      return store.getters[TITLE_MODULE + "/" + TITLE_ITEMS];
    },

    // Kriter seçenekleri
    criterionOptions() {
      return [
        { id: 'position', name: this.$t('general.position') },
        { id: 'branch', name: this.$t('general.branch') },
        { id: 'department', name: this.$t('general.department') },
        { id: 'region', name: this.$t('general.region') },
        { id: 'group', name: this.$t('general.group') },
        { id: 'title', name: this.$t('general.job_title') }
      ];
    }
  },

  methods: {
    //  tip isimlerini döndür
    getTypeDisplayName(type) {
      const option = this.criterionOptions.find(opt => opt.id === type);
      return option ? option.name : type;
    },

    // Kriter değerlerini getir
    async fetchCriterionValues(type, companyId = null) {
      const filters = companyId ? { company_id: companyId } : { all: 'all' };

      try {
        switch (type) {
          case 'position':
            await this.$store.dispatch(POSITION_MODULE + "/" + GET_POSITIONS, {
              url: 'api/position',
              filters: filters
            });
            return Array.isArray(this.positions) ? this.positions : [];

          case 'branch':
            await this.$store.dispatch(BRANCH_MODULE + "/" + GET_BRANCHES, {
              url: 'api/branch',
              filters: filters
            });
            return Array.isArray(this.branches) ? this.branches : [];

          case 'department':
            await this.$store.dispatch(DEPARTMENT_MODULE + "/" + GET_DEPARTMENTS, {
              url: 'api/department',
              filters: filters
            });
            return Array.isArray(this.departments) ? this.departments : [];

          case 'region':
            await this.$store.dispatch(REGION_MODULE + "/" + GET_REGIONS, {
              url: 'api/region',
              filters: filters
            });
            return Array.isArray(this.regions) ? this.regions : [];

          case 'group':
            await this.$store.dispatch(GROUP_MODULE + "/" + GET_GROUPS, {
              url: 'api/group',
              filters: { all: 'all' }
            });
            return Array.isArray(this.groups) ? this.groups : [];

          case 'title':
            await this.$store.dispatch(TITLE_MODULE + "/" + GET_TITLES, {
              url: 'api/title',
              filters: filters
            });
            return Array.isArray(this.titles) ? this.titles : [];

          default:
            return [];
        }
      } catch (error) {
        console.error(`Error fetching ${type} values:`, error);
        return [];
      }
    },

    // Tüm referans verileri getir (liste sayfası için)
    async fetchAllReferenceData() {
      try {
        await Promise.all([
          this.fetchCriterionValues('position'),
          this.fetchCriterionValues('branch'),
          this.fetchCriterionValues('department'),
          this.fetchCriterionValues('region'),
          this.fetchCriterionValues('group'),
          this.fetchCriterionValues('title')
        ]);
      } catch (error) {
        console.error('Error fetching reference data:', error);
      }
    },

    // ID'leri isimlere çevir
    resolveIdsToNames(type, ids, resolvedData = null) {
      if (!ids || !Array.isArray(ids)) return 'Veri yok';

      let referenceData = resolvedData || [];
      
      switch (type) {
        case 'position':
          referenceData = resolvedData || this.positions || [];
          break;
        case 'branch':
          referenceData = resolvedData || this.branches || [];
          break;
        case 'department':
          referenceData = resolvedData || this.departments || [];
          break;
        case 'region':
          referenceData = resolvedData || this.regions || [];
          break;
        case 'group':
          referenceData = resolvedData || this.groups || [];
          break;
        case 'title':
          referenceData = resolvedData || this.titles || [];
          break;

        default:
          return ids.join(', ');
      }

      // ID'leri isimlere çevir
      const names = ids.map(id => {
        const item = referenceData.find(ref => ref.id == id);
        return item ? item.name : `ID ${id}`;
      });

      return names.join(', ');
    },

    // Data'yı isimlerle formatla
    formatDataWithNames(item, resolvedData = null) {
      if (!item.data) return 'Veri yok';

      if (Array.isArray(item.data)) {
        return this.resolveIdsToNames(item.type, item.data, resolvedData);
      }

      if (typeof item.data === 'object') {
        const ids = Object.values(item.data);
        return this.resolveIdsToNames(item.type, ids, resolvedData);
      }

      // Tek bir ID ise
      return this.resolveIdsToNames(item.type, [item.data], resolvedData);
    }
  }
}; 